import { cn } from "@/lib/utils";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { unitOptions } from "@/consts";

import { UseFormReturn } from "react-hook-form";
import { BiDuplicate } from "react-icons/bi";
import DeleteIcon from "@mui/icons-material/Delete";
import TenderFieldView from "@/pages/vuetender/components/tender-field-view";

interface Product {
  item: string;
  quantity: number;
  notice: string;
  unit: string;
  attachment: File | null;
}

function ItemsTender({
  setProducts,
  products,
  disabled,
  form,
}: {
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  form: UseFormReturn<any>;
  disabled?: boolean;
}) {
  const removeProduct = (index: number) => {
    const currentProducts = form.getValues("products") as Product[];
    if (currentProducts.length <= 1) {
      return;
    }
    const newProducts = currentProducts.filter((_, i) => i !== index);
    setProducts(newProducts);
    form.setValue("products", newProducts);
  };

  const copyProduct = (index: number) => {
    const currentProducts = form.getValues("products") as Product[];
    const productToCopy = { ...currentProducts[index] };

    const newProduct = {
      item: productToCopy.item,
      quantity: productToCopy.quantity,
      notice: productToCopy.notice,
      unit: productToCopy.unit,
      attachment: null,
    };

    const updatedProducts = [
      ...currentProducts.slice(0, index + 1),
      newProduct,
      ...currentProducts.slice(index + 1),
    ];

    setProducts(updatedProducts);
    form.setValue("products", updatedProducts);
  };

  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  return (
    <div className="w-full">
      {/* Desktop Table View */}
      <div className="hidden lg:block">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
            <div className="grid grid-cols-6 gap-4 px-6 py-4">
              <div className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                {t("offers.item")}
              </div>
              <div className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                {t("checkout.fields.quantity")}
              </div>
              <div className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                {t("item.fields.unit")}
              </div>
              <div className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                {t("orders.fields.notes")}
              </div>
              <div className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                {t("offers.attachment")}
              </div>
              <div className="font-semibold text-gray-700 text-sm uppercase tracking-wide text-center">
                {t("actions.Action")}
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100">
            {products.map((_, index) => (
              <div
                key={index}
                className="grid grid-cols-6 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors duration-150"
              >
                <div className="flex items-center">
                  <TenderFieldView
                    name={`products.${index}.item`}
                    placeholder={t("offers.placeholders.EnterItemName")}
                    label=""
                    inputType="text"
                    formControl={form.control}
                    disabled={disabled}
                  />
                </div>
                <div className="flex items-center">
                  <TenderFieldView
                    name={`products.${index}.quantity`}
                    label=""
                    disabled={disabled}
                    placeholder={t("offers.placeholders.EnterQuantity")}
                    inputType="number"
                    formControl={form.control}
                  />
                </div>
                <div className="mt-5">
                  <TenderFieldView
                    name={`products.${index}.unit`}
                    label=""
                    disabled={disabled}
                    inputType="combobox"
                    placeholder=""
                    formControl={form.control}
                    options={unitOptions}
                    showIcons={true}
                  />
                </div>
                <div className="flex items-center">
                  <TenderFieldView
                    disabled={disabled}
                    name={`products.${index}.notice`}
                    label=""
                    placeholder={t("tender.fields.EnterNote")}
                    inputType="textarea"
                    formControl={form.control}
                  />
                </div>
                <div className="flex items-center">
                  <TenderFieldView
                    disabled={disabled}
                    name={`products.${index}.attachment`}
                    placeholder={t("offers.placeholders.SelectFile")}
                    label=""
                    inputType="file"
                    formControl={form.control}
                  />
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => removeProduct(index)}
                    className={cn(
                      "p-2 rounded-lg transition-all duration-200 hover:bg-red-50",
                      index === 0 && products.length === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-red-500 hover:text-red-600"
                    )}
                    disabled={index === 0 && products.length === 1}
                  >
                    <DeleteIcon className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => copyProduct(index)}
                    className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
                  >
                    <BiDuplicate className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {products.map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4"
          >
            {/* Item Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("offers.item")}
              </label>
              <TenderFieldView
                name={`products.${index}.item`}
                placeholder={t("offers.placeholders.EnterItemName")}
                label=""
                inputType="text"
                formControl={form.control}
                disabled={disabled}
              />
            </div>

            {/* Quantity and Unit Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("checkout.fields.quantity")}
                </label>
                <TenderFieldView
                  name={`products.${index}.quantity`}
                  label=""
                  disabled={disabled}
                  placeholder={t("offers.placeholders.EnterQuantity")}
                  inputType="number"
                  formControl={form.control}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("item.fields.unit")}
                </label>
                <TenderFieldView
                  name={`products.${index}.unit`}
                  label=""
                  disabled={disabled}
                  inputType="combobox"
                  placeholder=""
                  formControl={form.control}
                  options={unitOptions}
                  showIcons={true}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("orders.fields.notes")}
              </label>
              <TenderFieldView
                disabled={disabled}
                name={`products.${index}.notice`}
                label=""
                placeholder={t("tender.fields.EnterNote")}
                inputType="textarea"
                formControl={form.control}
              />
            </div>

            {/* Attachment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("offers.attachment")}
              </label>
              <TenderFieldView
                disabled={disabled}
                name={`products.${index}.attachment`}
                placeholder={t("offers.placeholders.SelectFile")}
                label=""
                inputType="file"
                formControl={form.control}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => removeProduct(index)}
                className={cn(
                  "px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2",
                  index === 0 && products.length === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-red-50 text-red-600 hover:bg-red-100"
                )}
                disabled={index === 0 && products.length === 1}
              >
                <DeleteIcon className="w-4 h-4" />
                <span className="text-sm font-medium">Delete</span>
              </button>
              <button
                type="button"
                onClick={() => copyProduct(index)}
                className="px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2"
              >
                <BiDuplicate className="w-4 h-4" />
                <span className="text-sm font-medium">Copy</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ItemsTender;
